/**
 * Calendar dates (VTK-1953).
 *
 * A hire date is a calendar date — no time of day, and it must mean the same day to every viewer.
 * The date inputs in this app store MM/DD/YYYY; the API takes YYYY-MM-DD. Converting between them
 * by digit rather than through `new Date(string)` is the point: `new Date('01/15/2026')` is LOCAL
 * midnight while `new Date('2026-01-15')` is UTC midnight, and re-reading either in the other's
 * frame shifts the day. That mismatch is what made a date of birth render a day early for viewers
 * east of Greenwich (VTK-1915), and there is no reason to invite it back in for hire dates.
 */

const US_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  // Round-tripping catches overflow: Date.UTC rolls 2026-02-30 forward to 2026-03-02.
  const asUtc = new Date(Date.UTC(year, month - 1, day));
  return (
    asUtc.getUTCFullYear() === year &&
    asUtc.getUTCMonth() === month - 1 &&
    asUtc.getUTCDate() === day
  );
}

/** True for a strict MM/DD/YYYY string that is also a real date — the masked input allows 02/30/2026. */
export function isUsCalendarDate(value: string | undefined | null): boolean {
  if (!value) return false;
  const parts = US_DATE.exec(value);
  if (!parts) return false;
  return isRealDate(Number(parts[3]), Number(parts[1]), Number(parts[2]));
}

/** MM/DD/YYYY → the YYYY-MM-DD the API takes. Null when the input is not a real date. */
export function usDateToCalendarDate(value: string | undefined | null): string | null {
  if (!isUsCalendarDate(value)) return null;
  const [, month, day, year] = US_DATE.exec(value as string)!;
  return `${year}-${month}-${day}`;
}

/** YYYY-MM-DD → the MM/DD/YYYY the date inputs hold. Null when the input is not a real date. */
export function calendarDateToUsDate(value: string | undefined | null): string | null {
  if (!value) return null;
  const parts = CALENDAR_DATE.exec(value);
  if (!parts) return null;
  const [, year, month, day] = parts;
  if (!isRealDate(Number(year), Number(month), Number(day))) return null;
  return `${month}/${day}/${year}`;
}

/**
 * Whole days from today to a MM/DD/YYYY date — negative for the past. Both sides are compared as
 * local calendar days, so "today" is never off by the current time of day.
 */
export function daysFromTodayUs(value: string): number | null {
  if (!isUsCalendarDate(value)) return null;
  const [, month, day, year] = US_DATE.exec(value)!;
  const target = new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((target - today) / 86_400_000);
}
